import { isAfter } from 'date-fns';
import { GlobalError, get, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '@anytag/shared-atoms';
import { SingleDatePickerField, SingleDatePickerFieldProps } from '@anytag/shared-molecules';
import { MarketplaceFormKeys } from '../types';

export interface CampaignPeriodFieldProps<T extends string = keyof typeof MarketplaceFormKeys> {
  required?: boolean;
  disabled?: boolean;
  endDateProps?: Partial<Omit<SingleDatePickerFieldProps<T>, 'required'>>;
  startDateProps?: Partial<Omit<SingleDatePickerFieldProps<T>, 'required'>>;
}

export const CampaignPeriodField = <T extends string = keyof typeof MarketplaceFormKeys>({
  disabled,
  required,
  endDateProps,
  startDateProps
}: CampaignPeriodFieldProps<T>) => {
  const { t } = useTranslation();
  const {
    watch,
    register,
    setValue,
    formState: { errors }
  } = useFormContext<Record<string, string>>();

  const endDateName = (endDateProps?.name || 'period.endDate') as T;
  const startDateName = (startDateProps?.name || 'period.startDate') as T;
  const [startDateValue, endDateValue, postTrackingEndDateValue] = watch([
    startDateName,
    endDateName,
    MarketplaceFormKeys.postTrackingEndDate
  ]);
  const { ref } = register(startDateName);

  const today = new Date();
  const endDateError = get(errors, endDateName) as GlobalError;

  return (
    <>
      <SingleDatePickerField<T>
        required={required}
        disabledDays={{ before: new Date() }}
        placeholder={t<string>('Choose date')}
        title={t('Posting Start Date')}
        {...startDateProps}
        name={startDateName}
        disabled={startDateProps?.disabled || disabled}
        onChange={(field, value) => {
          if (value !== startDateValue) {
            setValue<string>(field, value, { shouldValidate: true });

            if (isAfter(value, endDateValue)) {
              setValue<string>(endDateName, '', { shouldValidate: true });
              setValue<string>(MarketplaceFormKeys.postTrackingEndDate, '', { shouldValidate: true });
            }
          }
        }}
      />
      <Form.Focus ref={ref} />
      <SingleDatePickerField<T>
        hasError={!!endDateError}
        help={t('Tooltip.Campaign Duration')}
        title={t('Posting End Date')}
        placeholder={t<string>('Choose date')}
        required={required && !endDateProps?.optional}
        disabledDays={{
          before: startDateValue && isAfter(startDateValue, today) ? new Date(startDateValue) : today
        }}
        {...endDateProps}
        name={endDateName}
        disabled={endDateProps?.disabled || disabled}
        onChange={(field, value) => {
          if (value !== endDateValue) {
            setValue<string>(field, value, { shouldValidate: true });

            if (isAfter(value, postTrackingEndDateValue)) {
              setValue<string>(MarketplaceFormKeys.postTrackingEndDate, '', { shouldValidate: true });
            }
          }
        }}
      />
    </>
  );
};
